import { useAuth0 } from "@auth0/auth0-react";
import { LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ArrowSquareLeft, Cog, Gift } from "@/components/icons";
import { Button } from "@/components/ui/button";
import Typography from "@/components/ui/typography";
import ReferEarnDialog from "@/features/founders/components/ReferEarnDialog";
import ReferFounderDialog from "@/features/mentors/components/ReferFounderDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";
import { track } from "@/utils/mixpanel";
import { getUserRole } from "@/utils/users";

import { NavLink } from "./NavLink";
import { NavLinksProps } from "./types";

export const NavLinks = ({
  navItems,
  isActive,
  collapsed,
  mobile = false,
  onMobileMenuClose,
  handleLogout,
}: NavLinksProps) => {
  const { user } = useAuth0();
  const { user: currentUser } = useCurrentUser();
  const userRole = getUserRole(user);
  const navigate = useNavigate();
  const isMentor = userRole === "mentor";
  const isNonSubscribedLicensee =
    userRole === "licensee" && currentUser && !currentUser.subscriptionActive;
  const handleLogoutClick = () => {
    track("logout_clicked");
    handleLogout();
  };

  const settingsConfig = {
    admin: { name: "Admin Settings", path: "/admin-settings" },
    mentor: { name: "Settings", path: "/mentor/settings" },
    licensee: { name: "Settings", path: "/founder/settings" },
  };

  const settingsRoute =
    settingsConfig[userRole as keyof typeof settingsConfig] ||
    settingsConfig.licensee;

  return (
    <div
      className={cn(
        "flex flex-col w-full justify-between h-full",
        mobile && "mt-6",
      )}
    >
      <div className="flex flex-col gap-1 w-full">
        {navItems.map((item, index) => (
          <NavLink
            key={item.path}
            item={item}
            isActive={isActive}
            collapsed={collapsed}
            mobile={mobile}
            onClick={mobile ? onMobileMenuClose : undefined}
          />
        ))}
      </div>
      <div className="flex flex-col w-full gap-2">
        {/* Only show referral buttons for subscribed users */}
        {!isNonSubscribedLicensee && (
          <>
            {isMentor ? (
              <ReferFounderDialog>
                <Button
                  variant="ghost"
                  className={cn(
                    "flex items-center gap-2 h-12 rounded-lg transition-all duration-200 text-[#767677] hover:bg-[#f0f0f0] hover:text-[#767677] w-full",
                    collapsed && !mobile
                      ? "justify-center px-3"
                      : "justify-start px-6 py-4",
                  )}
                >
                  <Gift className="!h-6 !w-6" />
                  {(!collapsed || mobile) && (
                    <Typography size="l1" className="font-semibold" as="span">
                      Refer founder
                    </Typography>
                  )}
                </Button>
              </ReferFounderDialog>
            ) : (
              <ReferEarnDialog>
                <Button
                  variant="ghost"
                  className={cn(
                    "flex items-center gap-2 h-12 rounded-lg transition-all duration-200 text-[#767677] hover:bg-[#f0f0f0] hover:text-[#767677] w-full",
                    collapsed && !mobile
                      ? "justify-center px-3"
                      : "justify-start px-6 py-4",
                  )}
                >
                  <Gift className="!h-6 !w-6" />
                  {(!collapsed || mobile) && (
                    <Typography size="l1" className="font-semibold" as="span">
                      Refer & Earn
                    </Typography>
                  )}
                </Button>
              </ReferEarnDialog>
            )}
          </>
        )}

        <NavLink
          item={{
            name: settingsRoute.name,
            path: settingsRoute.path,
            icon: <Cog className="!h-6 !w-6" />,
            roles: ["mentor", "licensee", "admin"],
            onClick: () => navigate(settingsRoute.path),
          }}
          isActive={isActive}
          collapsed={collapsed}
          mobile={mobile}
        />

        <NavLink
          item={{
            name: "Logout",
            path: "#",
            icon: <ArrowSquareLeft className="!h-6 !w-6" />,
            roles: ["mentor", "licensee", "admin"],
            onClick: handleLogoutClick,
          }}
          isActive={() => false}
          collapsed={collapsed}
          mobile={mobile}
        />
      </div>
    </div>
  );
};
